import { Container } from './Container'
import { Text } from './Text'

/**
 * Frame for a screen whose body is an externally-hosted document — the EDF NDA,
 * the application, or the Omnidek pre-qualification assessment. Drops the
 * content into a thin orange-bordered box (Container surface="external") so it
 * reads as embedded external content rather than a native app screen.
 *
 * Two body modes:
 *   - pass `src` → renders an <iframe> filling the box (the live embed).
 *   - pass `placeholder` → renders a centered placeholder line (for screens
 *     whose source URL isn't wired yet).
 *
 * Two height modes via `fill`:
 *   - 'shell' (default) → mounted inside AppShell; fills the band between the
 *     dark Header (56px) and the TabBar footer (64px).
 *   - 'page' → fills its flex parent instead; the caller (e.g. the full-bleed
 *     web pre-qual flow, which has no Header/TabBar) supplies the height.
 *
 * Presentational only — callers pass already-translated strings.
 */
const CONTENT_MIN_HEIGHT =
  'calc(100dvh - (env(safe-area-inset-top, 0px) + 56px) - (64px + env(safe-area-inset-bottom, 0px)))'

// Geometry of the mask that hides the Omnidek "Sage" mark in the form footer.
//
// Omnidek zooms `app-root.app-viewport-scaled` to iframeWidth / 1920, so the
// footer (a 50px bar in that 1920-wide design) and the Sage image shrink with
// the frame. A fixed pixel mask is taller than the scaled footer and paints
// over the last lines of the form. These lengths are design pixels, resolved
// against the frame's width with cqw (the parent is an inline-size container):
//   visual = design * iframeWidth / 1920 = design / 1920 * 100cqw.
//
// Measured live (360–900px): the form column is 1200 design-px wide and
// centered, so its left edge sits at 360. The Sage image's right edge is
// ~335px into that column; 350 leaves a few pixels of slack and still ends
// well short of Submit, which stays on the right of the footer. Height matches
// the 50px footer so the mask stays inside the bar and does not cover fields.

// Geometry of the mask that hides the Omnidek sign-in option on the Omnidek
// login chooser (see `maskSsoOption`). Unlike the Sage bar above — which is
// anchored to a viewport edge — this target sits in normal flow, so the numbers
// come from the card's layout rather than an edge offset.
//
// The card is a vertically-centered stack:
//   .auth-client-image / .sso-text / .sso-button (Omnidek) / .sso-or / .sso-button (EDF)
// The mask spans the middle three: the "Click 'Log in with Omnidek' to use your
// existing credentials" copy (85px), the button itself (66px) and the "OR"
// divider (25px) = 176px, starting at the .sso-text top and ending flush with
// the top of "Login to EDF".
//
// Measured live at 1280x720, 375x812 and 686x594: the block starts at exactly
// `50% - 76px` of the iframe viewport at every height, so this needs no
// breakpoint tuning. The height doesn't drift with width either, because the
// card caps its content at 340px — the copy wraps identically at 375px and
// 1280px, which is what makes a fixed height safe here.
//
// Everything below the mask is left alone on purpose: "Login to EDF" stays
// visible and clickable, and the client logo above still shows.
const SSO_MASK_TOP = 'calc(50% - 76px)'
const SSO_MASK_WIDTH = 'min(340px, calc(100% - 40px))'
const SSO_MASK_HEIGHT = 176

export function EmbeddedDocFrame({
  title,
  placeholder,
  src,
  fill = 'shell',
  maskBottomBar = false,
  maskSsoOption = false,
}: {
  title: string
  /** Placeholder line shown when `src` is absent. */
  placeholder?: string
  /** External document URL; when set, renders an <iframe> filling the box. */
  src?: string
  /** Height strategy: 'shell' fills the AppShell band, 'page' fills the flex parent. */
  fill?: 'shell' | 'page'
  /**
   * Cover the bottom-left of the embed with an opaque bar. Used to hide the
   * Omnidek "Sage" sticky bar (which we can't touch — it lives inside the
   * cross-origin iframe) while leaving the form's Submit control, on the right,
   * clickable. Sized to the scaled footer — see the Sage mask notes above.
   */
  maskBottomBar?: boolean
  /**
   * On the Omnidek login chooser, cover the "Click 'Log in with Omnidek' to use
   * your existing credentials" copy, the "Log in with Omnidek" button, and the
   * "OR" divider beneath it — leaving "Login to EDF" as the only offered route
   * and the client logo still showing above. Cosmetic only: the button still
   * exists in the cross-origin document; the opaque overlay hides it and
   * swallows its clicks, nothing more.
   *
   * KNOWN DEAD END — do not wire this into a user-facing screen without first
   * resolving how login hands off. The chooser page frames fine (it sends no
   * X-Frame-Options and no frame-ancestors), but "Login to EDF" then navigates
   * the frame to Keycloak, which sends `x-frame-options: SAMEORIGIN` and
   * `frame-ancestors 'self'` and refuses to render framed. The mask works; the
   * login it leads to does not.
   *
   * Two ways out, neither of them code in this file: relax `frame-ancestors` on
   * the edfkey realm (Realm Settings → Security Defenses — that Keycloak is
   * ours, though the flow then still breaks wherever third-party cookies are
   * blocked), or get an Auth0 `connection` deep link from Omnidek, which skips
   * the chooser and makes the mask unnecessary.
   */
  maskSsoOption?: boolean
}) {
  const isPage = fill === 'page'
  return (
    <section
      className={`flex flex-col px-4 py-4${isPage ? ' flex-1' : ''}`}
      style={isPage ? undefined : { minHeight: CONTENT_MIN_HEIGHT }}
    >
      <Text as="h1" variant="h2" weight="bold" className="mb-3">
        {title}
      </Text>
      <Container
        surface="external"
        className={`@container relative flex flex-1 flex-col overflow-hidden${src ? '' : ' overflow-y-auto p-5'}`}
      >
        {src ? (
          <iframe
            src={src}
            title={title}
            className="h-full w-full flex-1 rounded-[inherit] border-0"
            allow="clipboard-write"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <Text as="p" variant="sm" className="m-auto text-center text-gray-600">
            {placeholder}
          </Text>
        )}
        {src && maskBottomBar && (
          <div
            aria-hidden
            className="absolute bottom-0 z-10 bg-white"
            style={{
              left: 'calc(100cqw * 360 / 1920)',
              width: 'calc(100cqw * 350 / 1920)',
              height: 'calc(100cqw * 50 / 1920)',
            }}
          />
        )}
        {src && maskSsoOption && (
          // bg-white matches the chooser card (.auth-content-white is #FFF), so the
          // mask reads as empty card rather than a pasted-on slab.
          <div
            aria-hidden
            className="absolute left-1/2 z-10 -translate-x-1/2 bg-white"
            style={{ top: SSO_MASK_TOP, width: SSO_MASK_WIDTH, height: SSO_MASK_HEIGHT }}
          />
        )}
      </Container>
    </section>
  )
}
